interface ActivePageLinkGraph {
  viewport: HTMLElement;
  svg: SVGSVGElement;
  placeholder: Comment;
  opener: HTMLButtonElement;
  baseWidth: number;
  baseHeight: number;
  originalSvgWidth: string;
  originalSvgHeight: string;
  originalScrollLeft: number;
  originalScrollTop: number;
  zoom: number;
}

interface PageLinkGraphPan {
  pointerId: number;
  viewport: HTMLElement;
  startClientX: number;
  startClientY: number;
  startScrollLeft: number;
  startScrollTop: number;
}

let activeGraph: ActivePageLinkGraph | null = null;
let activePan: PageLinkGraphPan | null = null;

function gallery(): HTMLDialogElement | null {
  return document.querySelector<HTMLDialogElement>("dialog[data-page-link-graph-gallery]");
}

export function closePageLinkGraph() {
  const graphGallery = gallery();
  if (graphGallery?.open) graphGallery.close();
  else restoreGraph();
}

export function initializePageLinkGraph() {
  const graphGallery = gallery();
  if (!graphGallery) return;

  document.addEventListener("click", event => {
    const target = event.target as Element | null;
    const opener = target?.closest<HTMLButtonElement>("[data-expand-page-link-graph]");
    if (opener) {
      event.preventDefault();
      openGraph(opener, graphGallery);
      return;
    }

    if (target?.closest("[data-close-page-link-graph]")) {
      closePageLinkGraph();
    } else if (target?.closest("[data-page-link-graph-zoom-out]")) {
      setZoom((activeGraph?.zoom ?? 1) / 1.25);
    } else if (target?.closest("[data-page-link-graph-zoom-in]")) {
      setZoom((activeGraph?.zoom ?? 1) * 1.25);
    } else if (target?.closest("[data-page-link-graph-fit]")) {
      fitGraph();
    }
  });

  graphGallery.addEventListener("close", restoreGraph);
  graphGallery.addEventListener("click", event => {
    if (event.target === graphGallery) graphGallery.close();
  });

  const stage = graphGallery.querySelector<HTMLElement>("[data-page-link-graph-gallery-stage]");
  if (!stage) throw new Error("The page-link graph gallery stage is missing.");
  stage.addEventListener("pointerdown", beginPan);
  stage.addEventListener("pointermove", movePan);
  stage.addEventListener("pointerup", endPan);
  stage.addEventListener("pointercancel", endPan);
  stage.addEventListener("lostpointercapture", endPan);
  stage.addEventListener("wheel", zoomWithWheel, { passive: false });
  window.addEventListener("beforeprint", closePageLinkGraph);
}

function openGraph(opener: HTMLButtonElement, graphGallery: HTMLDialogElement) {
  const drawer = opener.closest("dialog[data-page-links-drawer]");
  const viewport = drawer?.querySelector<HTMLElement>("[data-page-link-graph-viewport]");
  const svg = viewport?.querySelector<SVGSVGElement>(":scope > svg");
  const stage = graphGallery.querySelector<HTMLElement>("[data-page-link-graph-gallery-stage]");
  if (!viewport || !svg || !stage) return;

  if (activeGraph) restoreGraph();
  const viewBox = svg.viewBox.baseVal;
  const placeholder = document.createComment("qprins-page-link-graph-placeholder");
  viewport.replaceWith(placeholder);
  stage.replaceChildren(viewport);
  activeGraph = {
    viewport,
    svg,
    placeholder,
    opener,
    baseWidth: viewBox.width || 960,
    baseHeight: viewBox.height || 620,
    originalSvgWidth: svg.style.width,
    originalSvgHeight: svg.style.height,
    originalScrollLeft: viewport.scrollLeft,
    originalScrollTop: viewport.scrollTop,
    zoom: 1
  };
  viewport.dataset.pageLinkGraphExpanded = "true";
  opener.setAttribute("aria-expanded", "true");
  document.documentElement.dataset.pageLinkGraphOpen = "true";
  graphGallery.showModal();
  requestAnimationFrame(() => {
    if (activeGraph?.viewport !== viewport) return;
    fitGraph();
    viewport.focus({ preventScroll: true });
  });
}

function fitGraph() {
  const active = activeGraph;
  if (!active) return;
  const availableWidth = Math.max(1, active.viewport.clientWidth - 64);
  const availableHeight = Math.max(1, active.viewport.clientHeight - 64);
  const fitZoom = Math.min(
    availableWidth / active.baseWidth,
    availableHeight / active.baseHeight);
  setZoom(fitZoom, Math.min(0.15, fitZoom));
  active.viewport.scrollTo({ left: 0, top: 0 });
}

function setZoom(requestedZoom: number, minimumZoom = 0.15, anchor?: { x: number; y: number }) {
  const active = activeGraph;
  if (!active || !Number.isFinite(requestedZoom)) return;

  const previousZoom = active.zoom;
  const nextZoom = Math.min(3, Math.max(minimumZoom, requestedZoom));
  const viewportX = anchor?.x ?? active.viewport.clientWidth / 2;
  const viewportY = anchor?.y ?? active.viewport.clientHeight / 2;
  const graphX = (active.viewport.scrollLeft + viewportX) / previousZoom;
  const graphY = (active.viewport.scrollTop + viewportY) / previousZoom;
  active.zoom = nextZoom;
  active.svg.style.width = `${Math.round(active.baseWidth * nextZoom)}px`;
  active.svg.style.height = `${Math.round(active.baseHeight * nextZoom)}px`;
  const output = gallery()?.querySelector<HTMLOutputElement>("[data-page-link-graph-zoom]");
  if (output) output.textContent = `${Math.round(nextZoom * 100)}%`;
  active.viewport.scrollTo({
    left: Math.max(0, graphX * nextZoom - viewportX),
    top: Math.max(0, graphY * nextZoom - viewportY)
  });
}

function zoomWithWheel(event: WheelEvent) {
  const active = activeGraph;
  if ((!event.ctrlKey && !event.metaKey) || !active) return;
  const target = event.target as Element | null;
  if (target?.closest("[data-page-link-graph-viewport]") !== active.viewport) return;

  event.preventDefault();
  const rect = active.viewport.getBoundingClientRect();
  const delta = event.deltaY * (event.deltaMode === WheelEvent.DOM_DELTA_LINE
    ? 16
    : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
      ? Math.max(1, active.viewport.clientHeight)
      : 1);
  setZoom(
    active.zoom * Math.exp(-delta * 0.002),
    Math.min(0.15, active.zoom),
    { x: event.clientX - rect.left, y: event.clientY - rect.top });
}

function beginPan(event: PointerEvent) {
  const active = activeGraph;
  if (!active || event.button !== 0 || event.isPrimary === false) return;
  const target = event.target as Element | null;
  if (target?.closest("[data-page-link-graph-viewport]") !== active.viewport) return;

  stopPan();
  activePan = {
    pointerId: event.pointerId,
    viewport: active.viewport,
    startClientX: event.clientX,
    startClientY: event.clientY,
    startScrollLeft: active.viewport.scrollLeft,
    startScrollTop: active.viewport.scrollTop
  };
  active.viewport.dataset.pageLinkGraphPanning = "true";
  active.viewport.setPointerCapture(event.pointerId);
  active.viewport.focus({ preventScroll: true });
  event.preventDefault();
}

function movePan(event: PointerEvent) {
  const pan = activePan;
  if (!pan || pan.pointerId !== event.pointerId) return;
  pan.viewport.scrollTo({
    left: pan.startScrollLeft - (event.clientX - pan.startClientX),
    top: pan.startScrollTop - (event.clientY - pan.startClientY)
  });
  event.preventDefault();
}

function endPan(event: PointerEvent) {
  stopPan(event.pointerId);
}

function stopPan(pointerId?: number) {
  const pan = activePan;
  if (!pan || (pointerId !== undefined && pan.pointerId !== pointerId)) return;
  activePan = null;
  delete pan.viewport.dataset.pageLinkGraphPanning;
  if (pan.viewport.hasPointerCapture(pan.pointerId)) {
    pan.viewport.releasePointerCapture(pan.pointerId);
  }
}

function restoreGraph() {
  stopPan();
  const active = activeGraph;
  if (!active) return;
  activeGraph = null;
  active.placeholder.replaceWith(active.viewport);
  delete active.viewport.dataset.pageLinkGraphExpanded;
  active.svg.style.width = active.originalSvgWidth;
  active.svg.style.height = active.originalSvgHeight;
  active.viewport.scrollTo({ left: active.originalScrollLeft, top: active.originalScrollTop });
  active.opener.setAttribute("aria-expanded", "false");
  delete document.documentElement.dataset.pageLinkGraphOpen;
  if (active.opener.isConnected) active.opener.focus({ preventScroll: true });
}
